import axios from 'axios'

import { useSupabaseClient } from '@supabase/auth-helpers-react'

import { BACKOFFICE_API_URL } from '@/lib/constants'

// Mantine
import { notifications } from '@mantine/notifications'

// React
import { useState } from 'react'

export function useAxiosApi() {
    // Axios
    const axiosInstance = axios.create({
        baseURL: BACKOFFICE_API_URL,
    })

    // Supabase
    const supabase = useSupabaseClient()

    // Loading state
    const [loading, setLoading] = useState(false)

    // Session perdue (refresh token invalide, storage vidé par un autre onglet) :
    // on coupe la session en local. Le SIGNED_OUT remonte au
    // SessionContextProvider et AuthGuard réaffiche la page de login, au lieu
    // d'empiler des notifications d'erreur sur des requêtes qui ne partiront pas.
    const endSession = async () => {
        try {
            await supabase.auth.signOut({ scope: 'local' })
        } catch {
            // le token est déjà mort côté serveur : la session locale est
            // effacée quand même
        }
    }

    // Request function
    const request = async ({ endpoint, method = 'get', params, data, expectedStatuses = [] }) => {
        const debugRole = localStorage.getItem('debug_role')
        // Get user jwt token
        const { data: sessionData, error } = await supabase.auth.getSession()
        const accessToken = sessionData?.session?.access_token

        // Loading
        setLoading(true)

        // Send request only if the session is still usable
        if (!error && accessToken) {
            try {
                const result = await axiosInstance.request({
                    url: endpoint,
                    method,
                    headers: {
                        Authorization: `Bearer ${accessToken}`,
                        ...(debugRole && { 'X-Debug-Role': debugRole }),
                    },
                    params,
                    data,
                    validateStatus: (status) => (status >= 200 && status < 300) || expectedStatuses.includes(status),
                })

                setLoading(false)

                return result
            } catch (error) {
                // Jeton refusé par l'API : même traitement, retour au login
                if (error?.response?.status === 401) {
                    setLoading(false)
                    await endSession()

                    throw error
                }

                // Aucune réponse du serveur : toutes les requêtes de la page
                // échouent en même temps, et le détail est déjà donné par
                // l'écran « Serveur injoignable ». Un id fixe suffit à n'en
                // afficher qu'une — Mantine ignore un id déjà affiché — tout en
                // gardant un retour visible sur une action ponctuelle.
                if (!error?.response) {
                    notifications.show({
                        id: 'api-unreachable',
                        title: 'Serveur injoignable',
                        message: "Le back-office n'a pas pu joindre le serveur.",
                        color: 'red',
                        autoClose: 10000,
                    })

                    setLoading(false)

                    throw error
                }

                const message =
                    error?.response?.data?.error_message ??
                    error?.response?.data?.message ??
                    error?.response?.data?.error_code ??
                    'Une erreur est survenue lors de la requête.'

                // Mantine
                notifications.show({
                    title: 'Une erreur est survenue',
                    message,
                    color: 'red',
                    autoClose: 10000,
                })

                // Loading
                setLoading(false)

                throw error
            }
        } else {
            setLoading(false)
            await endSession()

            throw error ?? new Error('Session expirée')
        }
    }

    const requestPost = async (endpoint, data) => {
        return request({ endpoint, method: 'post', data })
    }

    const requestGet = async (endpoint, params, { expectedStatuses = [] } = {}) => {
        return request({ endpoint, method: 'get', params, expectedStatuses })
    }

    const requestPatch = async (endpoint, data) => {
        return request({ endpoint, method: 'patch', data })
    }

    const requestDelete = async (endpoint, data) => {
        return request({ endpoint, method: 'delete', data })
    }

    return {
        requestPost,
        requestGet,
        requestPatch,
        requestDelete,
        loading,
    }
}
